import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Check, Lightbulb, RotateCcw } from "lucide-react";
import type {
  Activity,
  OrderActivity as OrderActivityType,
  PromptLabActivity as PromptLabActivityType,
  QuizActivity as QuizActivityType,
} from "../content/types";
import type { ActivityState } from "../lib/progress";
import { shuffleQuizQuestion } from "../lib/quiz";

type ActivityProps = {
  activity: Activity;
  initialState?: ActivityState;
  onChange: (state: ActivityState, ready: boolean) => void;
};

export function ActivityPanel({
  activity,
  initialState,
  onChange,
}: ActivityProps) {
  if (activity.kind === "quiz") {
    return (
      <QuizActivity
        activity={activity}
        initialState={initialState}
        onChange={onChange}
      />
    );
  }
  if (activity.kind === "order") {
    return (
      <OrderActivity
        activity={activity}
        initialState={initialState}
        onChange={onChange}
      />
    );
  }
  return (
    <PromptLabActivity
      activity={activity}
      initialState={initialState}
      onChange={onChange}
    />
  );
}

function QuizActivity({
  activity,
  initialState,
  onChange,
}: {
  activity: QuizActivityType;
  initialState?: ActivityState;
  onChange: ActivityProps["onChange"];
}) {
  const questions = useMemo(
    () => activity.questions.map((question) => shuffleQuizQuestion(question, "module")),
    [activity.questions],
  );
  const [answers, setAnswers] = useState<Record<string, number>>(
    (initialState?.answers as Record<string, number> | undefined) ?? {},
  );

  const answer = (questionId: string, choiceIndex: number) => {
    const next = { ...answers, [questionId]: choiceIndex };
    setAnswers(next);
    onChange(
      { answers: next },
      activity.questions.every((question) => question.id in next),
    );
  };

  return (
    <section className="activity-card">
      <div className="activity-heading">
        <span className="activity-icon">
          <Lightbulb size={20} />
        </span>
        <div>
          <p className="eyebrow">Try it</p>
          <h2>{activity.title}</h2>
          <p>{activity.description}</p>
        </div>
      </div>
      <div className="question-list">
        {questions.map((question, questionIndex) => {
          const selected = answers[question.id];
          return (
            <fieldset className="question" key={question.id}>
              <legend>
                <span>{questionIndex + 1}</span>
                {question.prompt}
              </legend>
              <div className="choice-grid">
                {question.choices.map((choice, choiceIndex) => {
                  const isSelected = selected === choiceIndex;
                  const isCorrect =
                    isSelected && choiceIndex === question.correctIndex;
                  const isIncorrect =
                    isSelected && choiceIndex !== question.correctIndex;
                  return (
                    <label
                      className={`choice ${isCorrect ? "correct" : ""} ${
                        isIncorrect ? "incorrect" : ""
                      }`}
                      key={choice}
                    >
                      <input
                        type="radio"
                        name={question.id}
                        checked={isSelected}
                        onChange={() => answer(question.id, choiceIndex)}
                      />
                      <span>{choice}</span>
                      {isCorrect ? <Check size={18} aria-hidden="true" /> : null}
                    </label>
                  );
                })}
              </div>
              {selected !== undefined ? (
                <p
                  className={`feedback ${
                    selected === question.correctIndex ? "correct" : "incorrect"
                  }`}
                  role="status"
                >
                  <strong>
                    {selected === question.correctIndex
                      ? "That’s it."
                      : "Not quite."}
                  </strong>{" "}
                  {selected === question.correctIndex ? question.explanation : (question.incorrectExplanation ?? question.explanation)}
                </p>
              ) : null}
            </fieldset>
          );
        })}
      </div>
      {activity.footer ? <p className="activity-footer">{activity.footer}</p> : null}
    </section>
  );
}

function OrderActivity({
  activity,
  initialState,
  onChange,
}: {
  activity: OrderActivityType;
  initialState?: ActivityState;
  onChange: ActivityProps["onChange"];
}) {
  const startingOrder =
    (initialState?.order as string[] | undefined) ?? activity.items;
  const [items, setItems] = useState(startingOrder);
  const [checked, setChecked] = useState(
    Boolean(initialState?.correct as boolean | undefined),
  );
  const isCorrect = useMemo(
    () =>
      items.every((item, index) => item === activity.correctOrder[index]),
    [activity.correctOrder, items],
  );

  const move = (index: number, direction: -1 | 1) => {
    const destination = index + direction;
    if (destination < 0 || destination >= items.length) return;
    const next = [...items];
    [next[index], next[destination]] = [next[destination], next[index]];
    setItems(next);
    setChecked(false);
    onChange({ order: next, correct: false }, false);
  };

  const check = () => {
    setChecked(true);
    onChange({ order: items, correct: isCorrect }, isCorrect);
  };

  const reset = () => {
    setItems(activity.items);
    setChecked(false);
    onChange({ order: activity.items, correct: false }, false);
  };

  return (
    <section className="activity-card">
      <div className="activity-heading">
        <span className="activity-icon">
          <Lightbulb size={20} />
        </span>
        <div>
          <p className="eyebrow">Put it in practice</p>
          <h2>{activity.title}</h2>
          <p>{activity.description}</p>
        </div>
      </div>
      <ol className="order-list">
        {items.map((item, index) => (
          <li key={item}>
            <span>{index + 1}</span>
            <p>{item}</p>
            <div>
              <button
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label={`Move ${item} up`}
              >
                <ArrowUp size={17} />
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === items.length - 1}
                aria-label={`Move ${item} down`}
              >
                <ArrowDown size={17} />
              </button>
            </div>
          </li>
        ))}
      </ol>
      <div className="activity-actions">
        <button className="secondary-button" type="button" onClick={reset}>
          <RotateCcw size={16} /> Reset
        </button>
        <button className="primary-button" type="button" onClick={check}>
          Check the order
        </button>
      </div>
      {checked ? (
        <p
          className={`feedback ${isCorrect ? "correct" : "incorrect"}`}
          role="status"
        >
          <strong>{isCorrect ? "Strong sequence." : "Try another order."}</strong>{" "}
          {activity.explanation}
        </p>
      ) : null}
    </section>
  );
}

function PromptLabActivity({
  activity,
  initialState,
  onChange,
}: {
  activity: PromptLabActivityType;
  initialState?: ActivityState;
  onChange: ActivityProps["onChange"];
}) {
  const [values, setValues] = useState<Record<string, string>>(
    (initialState?.values as Record<string, string> | undefined) ?? {},
  );
  const completedFields = activity.fields.filter(
    (field) => (values[field.id] ?? "").trim().length >= 12,
  ).length;

  const update = (fieldId: string, value: string) => {
    const next = { ...values, [fieldId]: value };
    setValues(next);
    const complete = activity.fields.filter(
      (field) => (next[field.id] ?? "").trim().length >= 12,
    ).length;
    onChange({ values: next }, complete >= activity.minimumCompletedFields);
  };

  return (
    <section className="activity-card">
      <div className="activity-heading">
        <span className="activity-icon">
          <Lightbulb size={20} />
        </span>
        <div>
          <p className="eyebrow">Private prompt lab</p>
          <h2>{activity.title}</h2>
          <p>{activity.scenario}</p>
        </div>
      </div>
      <div className="privacy-note">
        Your draft stays in this browser. Do not enter real customer or production
        information.
      </div>
      <div className="prompt-grid">
        {activity.fields.map((field) => (
          <label key={field.id}>
            <span>
              <strong>{field.label}</strong>
              <small>{field.hint}</small>
            </span>
            <textarea
              value={values[field.id] ?? ""}
              placeholder={field.placeholder}
              onChange={(event) => update(field.id, event.target.value)}
              rows={4}
            />
          </label>
        ))}
      </div>
      <div className="completion-meter" aria-live="polite">
        <span
          style={{
            width: `${(completedFields / activity.fields.length) * 100}%`,
          }}
        />
        <p>
          {completedFields} of {activity.fields.length} prompt elements ready
        </p>
      </div>
    </section>
  );
}
